/**
 * Tool metadata utilities for timeline display
 * Provides tool categorization, naming, coloring, and icon selection
 */

import { createElement, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import {
  PiChatLight,
  PiListChecksLight,
  PiPhoneLight,
  PiNoteLight,
  PiTrashLight,
  PiPencilLight,
  PiPlusLight,
  PiCheckCircleLight,
  PiXCircleLight,
  PiMagnifyingGlassLight,
  PiDownloadLight,
  PiUploadLight,
  PiGearSixLight,
  PiEnvelopeLight,
  PiCalendarLight,
  PiUserLight,
  PiFunnelLight,
  PiDatabaseLight,
  PiArrowsClockwiseLight,
  PiPaperPlaneTiltLight,
} from 'react-icons/pi';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/**
 * Tool category definitions with their colors
 */
export const ToolCategory = {
  WHATSAPP: 'whatsapp',
  MESSAGING: 'messaging',
  TASKS: 'tasks',
  CALLS: 'calls',
  DATABASE: 'database',
  CALENDAR: 'calendar',
  EMAIL: 'email',
  CONTACT: 'contact',
  FUNNEL: 'funnel',
  SYSTEM: 'system',
  UNKNOWN: 'unknown',
} as const;

/** Supported visual categories for AI Agent actions. */
export type ToolCategoryValue =
  (typeof ToolCategory)[keyof typeof ToolCategory];

/**
 * Color mapping for each tool category
 */
const CATEGORY_COLORS: Record<ToolCategoryValue, string> = {
  [ToolCategory.WHATSAPP]: 'text-green-600',
  [ToolCategory.MESSAGING]: 'text-blue-600',
  [ToolCategory.TASKS]: 'text-purple-600',
  [ToolCategory.CALLS]: 'text-orange-600',
  [ToolCategory.DATABASE]: 'text-gray-600',
  [ToolCategory.CALENDAR]: 'text-indigo-600',
  [ToolCategory.EMAIL]: 'text-red-600',
  [ToolCategory.CONTACT]: 'text-pink-600',
  [ToolCategory.FUNNEL]: 'text-yellow-600',
  [ToolCategory.SYSTEM]: 'text-slate-600',
  [ToolCategory.UNKNOWN]: 'text-gray-500',
};

/**
 * Action types that can be extracted from tool names
 */
const ActionType = {
  LIST: 'list',
  GET: 'get',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  CANCEL: 'cancel',
  COMPLETE: 'complete',
  SEND: 'send',
  DRAFT: 'draft',
  SEARCH: 'search',
  FETCH: 'fetch',
  DOWNLOAD: 'download',
  UPLOAD: 'upload',
  SYNC: 'sync',
  UNKNOWN: 'unknown',
} as const;

type ActionTypeValue = (typeof ActionType)[keyof typeof ActionType];

/**
 * Icon mapping for each action type
 */
const ACTION_ICONS: Record<ActionTypeValue, IconType> = {
  [ActionType.LIST]: PiListChecksLight,
  [ActionType.GET]: PiDownloadLight,
  [ActionType.CREATE]: PiPlusLight,
  [ActionType.UPDATE]: PiPencilLight,
  [ActionType.DELETE]: PiTrashLight,
  [ActionType.CANCEL]: PiXCircleLight,
  [ActionType.COMPLETE]: PiCheckCircleLight,
  [ActionType.SEND]: PiPaperPlaneTiltLight,
  [ActionType.DRAFT]: PiPencilLight,
  [ActionType.SEARCH]: PiMagnifyingGlassLight,
  [ActionType.FETCH]: PiDownloadLight,
  [ActionType.DOWNLOAD]: PiDownloadLight,
  [ActionType.UPLOAD]: PiUploadLight,
  [ActionType.SYNC]: PiArrowsClockwiseLight,
  [ActionType.UNKNOWN]: PiGearSixLight,
};

/**
 * Category-specific icons (used when action-based icon isn't appropriate)
 */
const CATEGORY_ICONS: Record<ToolCategoryValue, IconType> = {
  [ToolCategory.WHATSAPP]: PiChatLight,
  [ToolCategory.MESSAGING]: PiEnvelopeLight,
  [ToolCategory.TASKS]: PiListChecksLight,
  [ToolCategory.CALLS]: PiPhoneLight,
  [ToolCategory.DATABASE]: PiDatabaseLight,
  [ToolCategory.CALENDAR]: PiCalendarLight,
  [ToolCategory.EMAIL]: PiEnvelopeLight,
  [ToolCategory.CONTACT]: PiUserLight,
  [ToolCategory.FUNNEL]: PiFunnelLight,
  [ToolCategory.SYSTEM]: PiGearSixLight,
  [ToolCategory.UNKNOWN]: PiNoteLight,
};

/**
 * Tool metadata including category, display name, color, and icon
 */
export interface ToolMetadata {
  /** Visual domain category inferred from the action ID. */
  category: ToolCategoryValue;
  /** Translated or normalized user-facing action name. */
  displayName: string;
  /** Text color associated with the action category. */
  color: string;
  /** Icon associated with the action verb or category. */
  icon: ReactNode;
}

/**
 * Determine tool category from tool name
 */
const getCategoryFromToolName = (toolName: string): ToolCategoryValue => {
  const lowerName = toolName.toLowerCase();

  if (lowerName.includes('whatsapp')) {
    return ToolCategory.WHATSAPP;
  }
  if (lowerName.includes('message') || lowerName.includes('sms')) {
    return ToolCategory.MESSAGING;
  }
  if (lowerName.includes('task')) {
    return ToolCategory.TASKS;
  }
  if (lowerName.includes('call') || lowerName.includes('phone')) {
    return ToolCategory.CALLS;
  }
  if (lowerName.includes('calendar') || lowerName.includes('schedule')) {
    return ToolCategory.CALENDAR;
  }
  if (lowerName.includes('email') || lowerName.includes('mail')) {
    return ToolCategory.EMAIL;
  }
  if (lowerName.includes('contact')) {
    return ToolCategory.CONTACT;
  }
  if (lowerName.includes('funnel') || lowerName.includes('pipeline')) {
    return ToolCategory.FUNNEL;
  }
  if (
    lowerName.includes('database') ||
    lowerName.includes('db') ||
    lowerName.includes('storage') ||
    lowerName.includes('kb') ||
    lowerName.includes('knowledge') ||
    lowerName.includes('product')
  ) {
    return ToolCategory.DATABASE;
  }

  return ToolCategory.UNKNOWN;
};

/**
 * Extract action type from tool name
 */
const getActionTypeFromToolName = (toolName: string): ActionTypeValue => {
  const lowerName = toolName.toLowerCase();

  // Check for action keywords
  if (lowerName.includes('list')) return ActionType.LIST;
  if (lowerName.includes('get') || lowerName.includes('fetch')) {
    return ActionType.GET;
  }
  if (lowerName.includes('create') || lowerName.includes('add')) {
    return ActionType.CREATE;
  }
  if (lowerName.includes('update') || lowerName.includes('edit')) {
    return ActionType.UPDATE;
  }
  if (lowerName.includes('delete') || lowerName.includes('remove')) {
    return ActionType.DELETE;
  }
  if (lowerName.includes('cancel')) return ActionType.CANCEL;
  if (lowerName.includes('complete') || lowerName.includes('finish')) {
    return ActionType.COMPLETE;
  }
  if (lowerName.includes('send')) return ActionType.SEND;
  if (lowerName.includes('draft')) return ActionType.DRAFT;
  if (lowerName.includes('search') || lowerName.includes('find')) {
    return ActionType.SEARCH;
  }
  if (lowerName.includes('download')) return ActionType.DOWNLOAD;
  if (lowerName.includes('upload')) return ActionType.UPLOAD;
  if (lowerName.includes('sync')) return ActionType.SYNC;

  return ActionType.UNKNOWN;
};

/**
 * Normalize and format tool name for display
 * Removes common prefixes and formats to Title Case
 */
const normalizeToolName = (toolName: string): string => {
  let normalized = toolName;

  // Remove common prefixes
  normalized = normalized
    .replace(/^llamadapro\./i, '')
    .replace(/^comos\./i, '')
    .replace(/^action\./i, '')
    .replace(/^comos\.employees\./i, '');

  // Split by dots or dashes
  const parts = normalized.split(/[._-]/);

  // Capitalize each part and join with spaces
  return parts
    .map((part) => {
      // Skip empty parts
      if (!part) return '';

      // Capitalize first letter
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .filter(Boolean)
    .join(' ');
};

const TOOL_NAME_TRANSLATION_KEYS: Partial<Record<string, TranslationKey>> = {
  'whatsapp.list-templates':
    'automations.aiAgents.metadata.tools.listWhatsappTemplates',
  'whatsapp.get-template':
    'automations.aiAgents.metadata.tools.getWhatsappTemplate',
  'whatsapp.send-template':
    'automations.aiAgents.metadata.tools.sendWhatsappTemplate',
  'comos.action.send-whatsapp-template':
    'automations.aiAgents.metadata.tools.sendWhatsappTemplate',
  'draft-and-send-message':
    'automations.aiAgents.metadata.tools.sendWhatsappMessage',
  'llamadapro.action.send-whatsapp':
    'automations.aiAgents.metadata.tools.sendWhatsappMessage',
  'comos.tasks.list': 'automations.aiAgents.metadata.tools.listTasks',
  'comos.tasks.create': 'automations.aiAgents.metadata.tools.createTask',
  'comos.tasks.update': 'automations.aiAgents.metadata.tools.updateTask',
  'comos.tasks.complete': 'automations.aiAgents.metadata.tools.completeTask',
  'comos.tasks.cancel': 'automations.aiAgents.metadata.tools.cancelTask',
  'comos.tasks.delete': 'automations.aiAgents.metadata.tools.deleteTask',
  'comos.tasks.get': 'automations.aiAgents.metadata.tools.getTask',
  'comos.tasks.replace-follow-up':
    'automations.aiAgents.metadata.tools.replaceFollowUp',
  'make-phone-call': 'automations.aiAgents.metadata.tools.makePhoneCall',
  'llamadapro.action.make-phone-call':
    'automations.aiAgents.metadata.tools.makePhoneCall',
  'update-contact-and-conversation-fields':
    'automations.aiAgents.metadata.tools.updateFields',
  'llamadapro.action.update-contact-and-conversation-fields':
    'automations.aiAgents.metadata.tools.updateContactConversationFields',
  'update-conversation-status':
    'automations.aiAgents.metadata.tools.updateStatus',
  'llamadapro.action.update-conversation-status':
    'automations.aiAgents.metadata.tools.updateConversationStatus',
  'context.get-funnel-statuses':
    'automations.aiAgents.metadata.tools.getFunnelStatuses',
  'comos.list-whatsapp-phones':
    'automations.aiAgents.metadata.tools.listWhatsappPhones',
  'comos.action.search-properties':
    'automations.aiAgents.metadata.tools.searchProperties',
  'comos.action.set-conversation-property-of-interest':
    'automations.aiAgents.metadata.tools.selectPropertyOfInterest',
  'llamadapro.action.escalate-conversation-to-human-assistance':
    'automations.aiAgents.metadata.tools.escalateToHumanAssistance',
  'llamadapro.action.mark-conversation-as-ready-to-schedule-visit':
    'automations.aiAgents.metadata.tools.readyToScheduleVisit',
  'comos.search-kb': 'automations.aiAgents.metadata.tools.searchKnowledgeBase',
  'comos.action.search-products':
    'automations.aiAgents.metadata.tools.searchProducts',
  'comos.action.create-product':
    'automations.aiAgents.metadata.tools.createProduct',
  'comos.action.delete-product':
    'automations.aiAgents.metadata.tools.deleteProduct',
  'comos.action.get-product': 'automations.aiAgents.metadata.tools.getProduct',
  'comos.action.import-products':
    'automations.aiAgents.metadata.tools.importProducts',
  'comos.action.list-products':
    'automations.aiAgents.metadata.tools.listProducts',
  'comos.action.update-product':
    'automations.aiAgents.metadata.tools.updateProduct',
  'comos.action.create-property':
    'automations.aiAgents.metadata.tools.createProperty',
  'comos.action.delete-property':
    'automations.aiAgents.metadata.tools.deleteProperty',
  'comos.action.get-property':
    'automations.aiAgents.metadata.tools.getProperty',
  'comos.action.import-prop360':
    'automations.aiAgents.metadata.tools.importProp360',
  'comos.action.import-properties':
    'automations.aiAgents.metadata.tools.importProperties',
  'comos.action.list-properties':
    'automations.aiAgents.metadata.tools.listProperties',
  'comos.action.update-property':
    'automations.aiAgents.metadata.tools.updateProperty',
  'comos.action.send-platform-email':
    'automations.aiAgents.metadata.tools.sendPlatformEmail',
  'comos.call-llm': 'automations.aiAgents.metadata.tools.callLanguageModel',
  'comos.create-conversation':
    'automations.aiAgents.metadata.tools.createConversation',
  'comos.create-conversation-async':
    'automations.aiAgents.metadata.tools.createConversation',
  'comos.employees.publish-employee-phase-tools-completed-event':
    'automations.aiAgents.metadata.tools.publishPhaseToolsCompleted',
  'comos.get-conversation':
    'automations.aiAgents.metadata.tools.getConversation',
  'comos.get-conversation-calls':
    'automations.aiAgents.metadata.tools.getConversationCalls',
  'conditional-gate': 'automations.aiAgents.metadata.tools.conditionalGate',
  crawl_website: 'automations.aiAgents.metadata.tools.crawlWebsite',
  'date-time': 'automations.aiAgents.metadata.tools.getDateTime',
  extract_webpage_content:
    'automations.aiAgents.metadata.tools.extractWebpageContent',
  'fetch-conversation-history':
    'automations.aiAgents.metadata.tools.fetchConversationHistory',
  'hubspot.action.get-contact':
    'automations.aiAgents.metadata.tools.getHubspotContact',
  'list-users': 'automations.aiAgents.metadata.tools.listUsers',
  'llamadapro.action.extract-fields-from-conversation':
    'automations.aiAgents.metadata.tools.extractConversationFields',
  'llamadapro.action.get-contact':
    'automations.aiAgents.metadata.tools.getContact',
  'llamadapro.action.get-contact-by-provider':
    'automations.aiAgents.metadata.tools.getContactByProvider',
  'llamadapro.action.get-contact-fields':
    'automations.aiAgents.metadata.tools.getContactFields',
  'llamadapro.action.get-conversation-fields':
    'automations.aiAgents.metadata.tools.getConversationFields',
  'llamadapro.action.get-conversation-messages':
    'automations.aiAgents.metadata.tools.getConversationMessages',
  'llamadapro.action.get-funnel-address':
    'automations.aiAgents.metadata.tools.getFunnelAddress',
  'llamadapro.action.get-funnel-addresses':
    'automations.aiAgents.metadata.tools.getFunnelAddresses',
  'llamadapro.action.update-contact':
    'automations.aiAgents.metadata.tools.updateContact',
  'llamadapro.action.update-conversation':
    'automations.aiAgents.metadata.tools.updateConversation',
  'random-number': 'automations.aiAgents.metadata.tools.randomNumber',
  'round-robin-pick': 'automations.aiAgents.metadata.tools.roundRobinPick',
  'run-parallel': 'automations.aiAgents.metadata.tools.runParallel',
  'run-workflow': 'automations.aiAgents.metadata.tools.runWorkflow',
  search_web: 'automations.aiAgents.metadata.tools.searchWeb',
  'select-from-list': 'automations.aiAgents.metadata.tools.selectFromList',
  'send-webhook': 'automations.aiAgents.metadata.tools.sendWebhook',
  'sync-hubspot-contact-with-conversation':
    'automations.aiAgents.metadata.tools.syncHubspotContact',
  'text-pattern-matcher':
    'automations.aiAgents.metadata.tools.matchTextPattern',
  wait: 'automations.aiAgents.metadata.tools.wait',
};

const CAPABILITY_TRANSLATION_KEYS: Partial<Record<string, TranslationKey>> = {
  'property-search': 'automations.aiAgents.metadata.tools.searchProperties',
  'message-send': 'automations.aiAgents.metadata.tools.sendWhatsappMessage',
  'crm-fields-update':
    'automations.aiAgents.metadata.tools.updateContactConversationFields',
  'phone-call': 'automations.aiAgents.metadata.tools.makePhoneCall',
  'task-create': 'automations.aiAgents.metadata.tools.createTask',
  'follow-up-replace': 'automations.aiAgents.metadata.tools.replaceFollowUp',
  'task-complete': 'automations.aiAgents.metadata.tools.completeTask',
  'task-cancel': 'automations.aiAgents.metadata.tools.cancelTask',
  'status-transition':
    'automations.aiAgents.metadata.tools.updateConversationStatus',
  'no-action': 'automations.aiAgents.metadata.capabilities.noAction',
  semantic: 'automations.aiAgents.metadata.capabilities.customAction',
};

const normalizeCapabilityName = (capability: string): string =>
  capability
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

/**
 * Resolves a structured operation capability to user-facing copy.
 *
 * @param capability - Stable operation capability identifier.
 * @param translate - Active locale translation function.
 * @returns Translated or normalized capability name.
 */
export const getCapabilityDisplayName = (
  capability: string,
  translate: I18nContextValue['t']
): string => {
  const translationKey = CAPABILITY_TRANSLATION_KEYS[capability];
  return translationKey
    ? translate(translationKey)
    : normalizeCapabilityName(capability);
};

/**
 * Gets presentation metadata for one AI Agent action.
 *
 * @param toolName - Stable action identifier.
 * @param translate - Optional active locale translation function.
 * @param fallbackDisplayName - Optional catalog title for custom actions.
 * @returns User-facing metadata shared by agent, execution, and assistance UI.
 */
export const getToolMetadata = (
  toolName: string,
  translate?: I18nContextValue['t'],
  fallbackDisplayName?: string
): ToolMetadata => {
  const category = getCategoryFromToolName(toolName);
  const actionType = getActionTypeFromToolName(toolName);

  // Prefer localized built-in names, then catalog titles for custom actions.
  const translationKey = TOOL_NAME_TRANSLATION_KEYS[toolName];
  const catalogDisplayName = fallbackDisplayName?.trim()
    ? fallbackDisplayName
    : undefined;
  const displayName =
    translationKey && translate
      ? translate(translationKey)
      : catalogDisplayName ?? normalizeToolName(toolName);

  // Get color from category
  const color = CATEGORY_COLORS[category];

  // Prioritize action-based icons and fall back to the domain category.
  const Icon =
    actionType !== ActionType.UNKNOWN
      ? ACTION_ICONS[actionType]
      : CATEGORY_ICONS[category];
  const icon = createElement(Icon, { className: 'w-4 h-4' });

  return {
    category,
    displayName,
    color,
    icon,
  };
};
